import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { useAuth } from '@/auth/useAuth';
import { useCan } from '@/auth/consoleCapabilityContext';
import { useActingOrg } from '@/auth/actingOrgContext';
import { useToast } from '@/components/ui';
import {
    createOrgAssignment,
    createOrganization,
    createOrgLevel,
    deactivateOrgLevel,
    deleteOrganization,
    fetchOrgAssignments,
    fetchOrgLevels,
    fetchOrganizations,
    fetchOrgTenants,
    fetchProvisioningStatus,
    OrgAdminApiError,
    revokeOrgAssignment,
    updateOrganization,
    type Organization,
    type OrgAssignment,
    type OrgLevel,
} from '@/api/orgAdmin';
import { useHierarchyDrag } from './useHierarchyDrag';
import { useOrgMove } from './useOrgMove';
import { useOrgUnitTypes } from './useOrgUnitTypes';
import { useOrgTree } from './useOrgTree';
import { findNode } from './dragRules';
import { slugify } from './orgText';
import type { LevelAccountBreakdown, OrganizationTreeNode } from './types';

/**
 * EAA-1818: view-model for `/console/hierarchy`.
 *
 * The tree is built from real `Organization.parent` rows, lazily, one level per
 * expansion — see `useOrgTree` for the loading model and the acting-org-as-root
 * decision, `useOrgMove` for the single shared move mutation, and `types.ts` for
 * why `OrgTenant` is no longer involved in the tree at all.
 *
 * PERMISSION MODEL — two distinct gates, and conflating them would either hide
 * work an org-admin may do or offer work the server will reject:
 *
 *   `canManage`     = `org:{actingOrgId}.manage`. Renaming an org (PATCH name).
 *                     Both super-admins and that org's own admin have it.
 *   `canManageTree` = `console.orgs.manage`. Creating a child org, moving an org
 *                     and deactivating one. This capability is issued to
 *                     super-admins ONLY (`SUPER_ADMIN_CONSOLE_CAPABILITIES` in
 *                     portal-api `directory/authz.py`), which is exactly the
 *                     server-side gate on all three of those endpoints. It is
 *                     read through `can()` rather than `MeAdminData.is_super_admin`
 *                     because D10 forbids the FE deriving behaviour from the
 *                     latter — the capability list is the server's answer to
 *                     "may this caller do this", and the only one this screen uses.
 *
 * Per-row account counts from frame 28 are NOT rendered: the only per-org count
 * portal-api exposes is `GET /organizations/{id}/provisioning-status`, so showing
 * one on every row would be one request per node. The real figure is fetched for
 * the SELECTED node (and for the org in a pending move/deactivate) and shown
 * there instead of being faked across the tree.
 */

export function errMessage(error: unknown, fallback: string): string {
    return error instanceof OrgAdminApiError ? error.message : fallback;
}

function computeLevelBreakdown(
    data: { results: OrgAssignment[]; count: number } | undefined
): LevelAccountBreakdown {
    const results = data?.results ?? [];
    const byLevelId: Record<string, number> = {};
    let noLevel = 0;
    for (const a of results) {
        if (!a.is_active) continue;
        if (a.level_id) {
            byLevelId[a.level_id] = (byLevelId[a.level_id] ?? 0) + 1;
        } else {
            noLevel += 1;
        }
    }
    return {
        byLevelId,
        noLevel,
        truncated: (data?.count ?? 0) > results.length,
        sampleSize: results.length,
        totalActive: data?.count ?? 0,
    };
}

export function useHierarchyPage() {
    const { t } = useTranslation();
    const can = useCan();
    const config = useRuntimeConfig();
    const { authenticated, accessToken } = useAuth();
    const acting = useActingOrg();
    const queryClient = useQueryClient();
    const toast = useToast();
    const orgId = acting?.actingOrgId ?? null;
    const org = acting?.actingOrg ?? null;

    const canView = can('console.orgs.view');
    const enabled = authenticated && Boolean(accessToken) && Boolean(orgId) && canView;
    const canManage = Boolean(orgId) && can(`org:${orgId}.manage`);
    const canManageTree = can('console.orgs.manage');

    /* ------------------------------------------------------------- org tree */

    const treeVM = useOrgTree({ apiBase: config.apiBase, accessToken, rootOrgId: orgId, enabled });
    const {
        rootOrg,
        tree,
        visibleNodes,
        knownOrgs,
        childrenByParent,
        rootTruncated,
        expandedIds,
        toggleExpanded,
        expandNode,
        collapseAll,
        invalidateParents,
        invalidateOrg,
        isLoadingRoot,
        isErrorRoot,
        rootOrgQuery,
    } = treeVM;

    /* ---------------------------------------------------------------- data */

    const levelsQuery = useQuery({
        queryKey: ['org-levels', accessToken, orgId],
        queryFn: () => fetchOrgLevels(config.apiBase, accessToken as string, orgId as string),
        enabled,
        retry: false,
    });

    // EAA-1818: a SEPARATE fetch that only feeds the tree view's "Tài khoản theo
    // Level" card. Capped at the max page size the backend allows (100) —
    // `LevelAccountBreakdown.truncated` tells the UI to say so honestly rather
    // than silently under-counting past that.
    const levelBreakdownQuery = useQuery({
        queryKey: ['org-assignments-breakdown', accessToken, orgId],
        queryFn: () =>
            fetchOrgAssignments(config.apiBase, accessToken as string, orgId as string, {
                page_size: 100,
                is_active: true,
            }),
        enabled,
        retry: false,
    });

    // EAA-1818: the `?? []` fallbacks are memoized because they feed useMemo dependency arrays below.
    // A bare `data ?? []` mints a fresh array on every render while the query is loading or errored, so
    // every downstream memo — the flattened drag-slot list, the per-Level breakdown — would recompute on
    // each render instead of only when the data actually changes.
    const levels = useMemo(() => levelsQuery.data?.results ?? [], [levelsQuery.data]);
    const activeLevels = useMemo(() => levels.filter((lv) => lv.is_active), [levels]);
    const levelBreakdown = useMemo(
        () => computeLevelBreakdown(levelBreakdownQuery.data),
        [levelBreakdownQuery.data]
    );

    /**
     * EAA-1818: the toolbar meta line from frame 29 ("EMG Hà Nội · 5 đơn vị ·
     * 4 Level"). The đơn vị figure counts LOADED descendants of the root and is
     * therefore a floor, not a total — the tree fetches one level per expansion
     * (see `useOrgTree`), so unexpanded branches are genuinely unknown here and
     * there is no whole-subtree count endpoint to ask. `unitCountPartial` says
     * so, and the toolbar renders "5+" rather than implying 5 is the total.
     * Counting only what is loaded keeps this consistent with the rest of the
     * screen, which never invents a number it has not fetched.
     */
    const { unitCount, unitCountPartial } = useMemo(() => {
        let count = 0;
        let partial = rootTruncated;
        const walk = (nodes: OrganizationTreeNode[]) => {
            for (const node of nodes) {
                count += 1;
                if (!node.childrenLoaded) partial = true;
                walk(node.children);
            }
        };
        // Skip the root itself — it is named separately in the same line.
        for (const root of tree) {
            if (!root.childrenLoaded) partial = true;
            walk(root.children);
        }
        return { unitCount: count, unitCountPartial: partial };
    }, [tree, rootTruncated]);

    /* ---------------------------------------------------------- selection */

    const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
    useEffect(() => {
        // Default to the root row so the detail panel is never blank on arrival.
        if (!selectedOrgId && rootOrg) setSelectedOrgId(rootOrg.id);
    }, [rootOrg, selectedOrgId]);
    const selectedNode = useMemo(
        () => (selectedOrgId ? findNode(tree, selectedOrgId) : null),
        [tree, selectedOrgId]
    );

    /**
     * Real per-organization figures for the selected node only. `provisioning-status`
     * is org-scoped, so this is that đơn vị's own active-assignment count — the
     * number frame 28 puts in the aside header.
     */
    const selectedCoverageQuery = useQuery({
        queryKey: ['org-provisioning', accessToken, selectedOrgId],
        queryFn: () =>
            fetchProvisioningStatus(config.apiBase, accessToken as string, selectedOrgId as string),
        enabled: enabled && Boolean(selectedOrgId),
        retry: false,
    });

    /**
     * EAA-1818: the aside's "ứng dụng trong phạm vi" chips, as far as the backend
     * can answer them. `GET /organizations/{id}/tenants` lists that org's
     * `OrganizationAppScope` rows for application='ach' ONLY — the view hardcodes
     * `ACH_APP_CODE` — and there is no endpoint listing scope rows for any other
     * application. So "does this đơn vị have ACH in scope?" is real and shown;
     * the mock's `lms`/`sis` chips have no source and are NOT invented. The card
     * says which of the two it is rather than implying the list is complete.
     */
    const selectedScopesQuery = useQuery({
        queryKey: ['org-tenants', accessToken, selectedOrgId],
        queryFn: () =>
            fetchOrgTenants(config.apiBase, accessToken as string, selectedOrgId as string),
        enabled: enabled && Boolean(selectedOrgId),
        retry: false,
    });
    const selectedAchScopes = useMemo(
        () => (selectedScopesQuery.data ?? []).filter((t) => t.is_active),
        [selectedScopesQuery.data]
    );

    /* ------------------------------------------------------------ search */

    const [searchQuery, setSearchQuery] = useState('');
    const normalizedSearch = searchQuery.trim();
    const searchActive = normalizedSearch.length > 0;

    /**
     * EAA-1818: search cannot be a client-side filter over the tree, because the
     * tree only holds the levels the user has expanded — a match three levels
     * down an unopened branch would simply not exist locally. It is therefore a
     * server query (`GET /organizations?search=`), rendered as frame 33's
     * flattened result list.
     *
     * KNOWN LIMIT, stated rather than papered over: the endpoint scopes results
     * to every org the CALLER can see, not to the acting org's subtree, and there
     * is no ancestor-path field on the rows. Each result therefore shows its
     * parent's name only when that parent happens to be loaded (`knownOrgs`) —
     * frame 33's dimmed ancestor context, to the extent the API supports it.
     */
    const searchResultsQuery = useQuery({
        queryKey: ['org-search', accessToken, normalizedSearch],
        queryFn: () =>
            fetchOrganizations(config.apiBase, accessToken as string, {
                search: normalizedSearch,
                pageSize: 50,
                ordering: 'name',
            }),
        enabled: enabled && searchActive,
        retry: false,
    });
    const searchResults = useMemo(
        () => searchResultsQuery.data?.results ?? [],
        [searchResultsQuery.data]
    );
    const searchTruncated = (searchResultsQuery.data?.count ?? 0) > searchResults.length;

    const matchesSearch = useCallback(
        (haystacks: Array<string | null | undefined>) =>
            !searchActive ||
            haystacks.some((h) => (h ?? '').toLowerCase().includes(normalizedSearch.toLowerCase())),
        [searchActive, normalizedSearch]
    );
    const visibleLevels = useMemo(
        () => levels.filter((lv) => matchesSearch([lv.name, lv.role_key])),
        [levels, matchesSearch]
    );

    /* --------------------------------------------------------------- move */

    const moveVM = useOrgMove({
        apiBase: config.apiBase,
        accessToken,
        toast,
        invalidateParents,
        invalidateOrg,
        expandNode,
    });
    const { requestMove } = moveVM;

    /**
     * Open the "Chuyển đến…" picker for a node. Separate state from
     * `pendingMove`: the picker is where a target is CHOSEN, `pendingMove` is a
     * target already chosen and awaiting confirmation, and the picker itself
     * confirms inline rather than stacking a second dialog on top of itself.
     */
    const [moveDialogNodeId, setMoveDialogNodeId] = useState<string | null>(null);
    const moveDialogNode = useMemo(
        () => (moveDialogNodeId ? findNode(tree, moveDialogNodeId) : null),
        [tree, moveDialogNodeId]
    );
    const openMoveDialog = useCallback((node: OrganizationTreeNode) => {
        setMoveDialogNodeId(node.id);
    }, []);
    const closeMoveDialog = useCallback(() => setMoveDialogNodeId(null), []);

    /**
     * EAA-1891: the CROSS-TREE adoption dialog, a separate piece of state from
     * `moveDialogNodeId` above because the two dialogs do different things and can
     * both be reached from the same row. "Chuyển đến…" re-parents inside the
     * current tree (`PATCH parent`); this one files the đơn vị under another tree
     * entirely (`POST .../move`) and migrates its unit-type vocabulary. One shared
     * state slot would make the row menu's two entries indistinguishable to the
     * renderer.
     *
     * Stores the ORGANIZATION rather than the node id, unlike the dialog above:
     * the adoption dialog needs only the row (name, code, current parent) and is a
     * shared component with no knowledge of this screen's tree, so resolving a
     * node id through `findNode` would be work for nobody.
     */
    const [adoptDialogOrg, setAdoptDialogOrg] = useState<Organization | null>(null);
    const openAdoptDialog = useCallback(
        (node: OrganizationTreeNode) => setAdoptDialogOrg(node.organization),
        []
    );
    const closeAdoptDialog = useCallback(() => setAdoptDialogOrg(null), []);

    /* ------------------------------------------------------------ unit types */

    /**
     * EAA-1838: the root org's unit-type vocabulary. Mounted here rather than
     * inside any one surface that renders it, because four separate surfaces read
     * the same list — the row badges, the create modal's picker, `MoveOrgDialog`'s
     * candidate labels and the drag rule below — and a query per surface would
     * fetch it four times.
     */
    const unitTypesVM = useOrgUnitTypes({
        apiBase: config.apiBase,
        accessToken,
        rootOrgId: orgId,
        enabled,
        toast,
        invalidateParents,
        invalidateOrg,
    });
    const { unitTypesById } = unitTypesVM;

    /* --------------------------------------------------------------- drag */

    const levelNames = useMemo(() => new Map(levels.map((lv) => [lv.id, lv.name])), [levels]);
    const visibleOrgIds = useMemo(() => visibleNodes.map((n) => n.id), [visibleNodes]);
    const visibleLevelIds = useMemo(() => visibleLevels.map((lv) => lv.id), [visibleLevels]);
    const drag = useHierarchyDrag({
        tree,
        rootOrgId: orgId,
        knownOrgs,
        unitTypesById,
        levelNames,
        visibleOrgIds,
        visibleLevelIds,
        requestMove,
        // EAA-1891: a cross-tree drop opens the adoption dialog instead of
        // reporting rule 2 as a dead end.
        onCrossTreeDrop: (node) => setAdoptDialogOrg(node.organization),
        canMove: canManageTree,
        toast,
    });

    /* ---------------------------------------------------- org create/rename */

    const invalidateLevels = () =>
        queryClient.invalidateQueries({ queryKey: ['org-levels', accessToken, orgId] });
    const invalidateAssignments = () => {
        queryClient.invalidateQueries({ queryKey: ['org-assignments', accessToken, orgId] });
        queryClient.invalidateQueries({
            queryKey: ['org-assignments-breakdown', accessToken, orgId],
        });
    };

    const [createForm, setCreateForm] = useState({
        parentId: '',
        parentName: '',
        parentUnitTypeId: null as string | null,
        name: '',
        code: '',
        // EAA-1838: REQUIRED, unlike the optional tier select it replaced. An
        // empty string is a not-yet-answered form, not a valid "no type" choice —
        // `onSubmitCreate` refuses to submit on it. Optionality is exactly what
        // let the old reparent rule skip itself on most real moves.
        unitTypeId: '',
    });
    const [createOpen, setCreateOpen] = useState(false);
    const [createError, setCreateError] = useState<string | null>(null);

    const openCreateChild = useCallback((node: OrganizationTreeNode) => {
        setCreateForm({
            parentId: node.id,
            parentName: node.organization.name,
            parentUnitTypeId: node.organization.unit_type,
            name: '',
            code: '',
            unitTypeId: '',
        });
        setCreateError(null);
        setCreateOpen(true);
    }, []);
    const closeCreate = useCallback(() => {
        setCreateOpen(false);
        setCreateError(null);
    }, []);
    const onCreateNameChange = useCallback((name: string) => {
        setCreateForm((prev) => ({ ...prev, name, code: slugify(name) }));
    }, []);

    /**
     * EAA-1818: creating a child đơn vị is TWO calls, and that is a real backend
     * constraint rather than a shortcut. `OrganizationCreateSerializer` accepts no
     * `parent` — verified in portal-api `directory/org_serializers.py` — so the
     * parent cannot be set at creation time even though the modal asks for it in
     * one form. The đơn vị is created at root, then immediately PATCHed under its
     * parent.
     *
     * EAA-1838: the TYPE, unlike the parent, now rides along on the CREATE call.
     * That ordering is deliberate, not incidental: `validate_parent_change` grades
     * containment using the org's own type, so a đơn vị created untyped and then
     * PATCHed under a parent would be graded with no type at all — the rule would
     * skip itself and admit a placement the vocabulary forbids. Sending the type
     * first means the very first placement is already graded.
     *
     * If the PATCH fails the đơn vị still exists as a root org, so the error says
     * so explicitly instead of implying nothing happened.
     */
    const createChildOrg = useMutation({
        mutationFn: async () => {
            const unitTypeId = createForm.unitTypeId || undefined;
            const created = await createOrganization(config.apiBase, accessToken as string, {
                code: createForm.code.trim(),
                name: createForm.name.trim(),
                ...(unitTypeId ? { unit_type: unitTypeId } : {}),
            });
            if (!createForm.parentId) return created;
            try {
                return await updateOrganization(config.apiBase, accessToken as string, created.id, {
                    parent: createForm.parentId,
                });
            } catch (error) {
                throw new OrgAdminApiError(
                    t('console.hierarchy.hooks.page.createPatchFailedError', {
                        name: created.name,
                        parentName: createForm.parentName,
                        errorMessage: errMessage(
                            error,
                            t('console.hierarchy.hooks.page.unknownErrorFallback')
                        ),
                    }),
                    error instanceof OrgAdminApiError ? error.code : 0
                );
            }
        },
        onSuccess: (created) => {
            setCreateOpen(false);
            toast.success(
                t('console.hierarchy.hooks.page.createSuccessToast', { name: created.name })
            );
            invalidateParents([createForm.parentId || null]);
            if (createForm.parentId) expandNode(createForm.parentId);
        },
        onError: (error) =>
            setCreateError(
                errMessage(error, t('console.hierarchy.hooks.page.createErrorFallback'))
            ),
    });

    const onSubmitCreate = useCallback(
        (e: FormEvent) => {
            e.preventDefault();
            if (!createForm.name.trim() || !createForm.code.trim()) {
                setCreateError(t('console.hierarchy.hooks.page.createValidationNameCode'));
                return;
            }
            // EAA-1838: the type is required ONLY once the org has a vocabulary
            // to pick from. No default type is seeded any more, so a brand-new
            // org legitimately has none — and hard-requiring one would leave an
            // org-admin (who may not author types) unable to create a đơn vị at
            // all. The server accepts an absent `unit_type`, leaving the FK null,
            // which is the same fully permissive state an untyped tree always had.
            //
            // Guarded here rather than relying on the <select required> alone,
            // since the picker's inline-create branch disables that select while a
            // draft is open.
            if (!createForm.unitTypeId && unitTypesVM.activeUnitTypes.length > 0) {
                setCreateError(t('console.hierarchy.hooks.page.createValidationUnitType'));
                return;
            }
            createChildOrg.mutate();
        },
        [createForm, createChildOrg, unitTypesVM.activeUnitTypes, t]
    );

    /**
     * EAA-1818: rename is INLINE at the row, not a modal — judgment call, taken
     * because frame 30 specifies it inline ("Đổi tên ngay tại dòng", Enter to
     * save / Esc to cancel) and because the cost is one text input and two
     * pieces of state, the row component already existing. The modal remains for
     * CREATE, which genuinely needs a second field (the immutable code) and a
     * parent context line.
     */
    const [renamingId, setRenamingId] = useState<string | null>(null);
    const [renameDraft, setRenameDraft] = useState('');
    const [renameError, setRenameError] = useState<string | null>(null);

    const beginRename = useCallback((node: OrganizationTreeNode) => {
        setRenamingId(node.id);
        setRenameDraft(node.organization.name);
        setRenameError(null);
    }, []);
    const cancelRename = useCallback(() => {
        setRenamingId(null);
        setRenameError(null);
    }, []);

    const renameOrg = useMutation({
        mutationFn: (vars: { orgId: string; name: string }) =>
            updateOrganization(config.apiBase, accessToken as string, vars.orgId, {
                name: vars.name,
            }),
        onSuccess: (organization) => {
            setRenamingId(null);
            setRenameError(null);
            toast.success(
                t('console.hierarchy.hooks.page.renameSuccessToast', { name: organization.name })
            );
            invalidateParents([organization.parent]);
            invalidateOrg(organization.id);
        },
        onError: (error) =>
            setRenameError(
                errMessage(error, t('console.hierarchy.hooks.page.renameErrorFallback'))
            ),
    });

    /**
     * Duplicate-name guard. portal-api enforces uniqueness on `code` only, NOT on
     * `name` — verified against `OrganizationCreateSerializer.validate_code`,
     * which has no `name` counterpart — so frame 30's "tên trùng trong cùng cấp
     * cha bị chặn khi lưu" has no server rule behind it. This is therefore a
     * client-side courtesy check against the siblings ALREADY LOADED for that
     * parent, and it is deliberately not presented as authoritative: an unloaded
     * or newly-created sibling will not be caught, and the server will accept
     * the duplicate if it slips through.
     */
    const commitRename = useCallback(() => {
        if (!renamingId) return;
        const node = findNode(tree, renamingId);
        const nextName = renameDraft.trim();
        if (!node || !nextName) {
            setRenameError(t('console.hierarchy.hooks.page.renameValidationRequired'));
            return;
        }
        if (nextName === node.organization.name) {
            setRenamingId(null);
            return;
        }
        const siblings = childrenByParent.get(node.organization.parent ?? '')?.children ?? [];
        const clash = siblings.some(
            (sib) => sib.id !== node.id && sib.name.trim().toLowerCase() === nextName.toLowerCase()
        );
        if (clash) {
            setRenameError(t('console.hierarchy.hooks.page.renameValidationDuplicate'));
            return;
        }
        renameOrg.mutate({ orgId: node.id, name: nextName });
    }, [renamingId, renameDraft, tree, childrenByParent, renameOrg, t]);

    /* ----------------------------------------------------------- deactivate */

    const [confirmDeactivateOrgId, setConfirmDeactivateOrgId] = useState<string | null>(null);
    const confirmDeactivateNode = useMemo(
        () => (confirmDeactivateOrgId ? findNode(tree, confirmDeactivateOrgId) : null),
        [tree, confirmDeactivateOrgId]
    );

    /**
     * Frame 32 shows a "124 tài khoản" figure BEFORE the action runs, but the
     * authoritative `accounts_affected` only exists in the DELETE response. The
     * dialog therefore states the org's active-assignment count from
     * `provisioning-status` (a real, pre-flight number, and labelled as that),
     * and the success toast reports the exact `accounts_affected` the server
     * returned.
     */
    const deactivatePreflightQuery = useQuery({
        queryKey: ['org-provisioning', accessToken, confirmDeactivateOrgId],
        queryFn: () =>
            fetchProvisioningStatus(
                config.apiBase,
                accessToken as string,
                confirmDeactivateOrgId as string
            ),
        enabled: Boolean(accessToken) && Boolean(confirmDeactivateOrgId),
        retry: false,
    });

    const deactivateOrg = useMutation({
        mutationFn: (targetId: string) =>
            deleteOrganization(config.apiBase, accessToken as string, targetId),
        onSuccess: (result) => {
            setConfirmDeactivateOrgId(null);
            toast.success(
                t('console.hierarchy.hooks.page.deactivateSuccessToast', {
                    name: result.organization.name,
                    value: result.accounts_affected.toLocaleString('vi-VN'),
                })
            );
            invalidateParents([result.organization.parent]);
            invalidateOrg(result.organization.id);
        },
        onError: (error) =>
            toast.error(
                errMessage(error, t('console.hierarchy.hooks.page.deactivateErrorFallback'))
            ),
    });

    const reactivateOrg = useMutation({
        mutationFn: (targetId: string) =>
            updateOrganization(config.apiBase, accessToken as string, targetId, {
                is_active: true,
            }),
        onSuccess: (organization) => {
            toast.success(
                t('console.hierarchy.hooks.page.reactivateSuccessToast', {
                    name: organization.name,
                })
            );
            invalidateParents([organization.parent]);
            invalidateOrg(organization.id);
        },
        onError: (error) =>
            toast.error(
                errMessage(error, t('console.hierarchy.hooks.page.reactivateErrorFallback'))
            ),
    });

    const copyOrgCode = useCallback(
        (code: string) => {
            void navigator.clipboard
                ?.writeText(code)
                .then(() =>
                    toast.success(
                        t('console.hierarchy.hooks.page.copyCodeSuccessToast', { code })
                    )
                )
                .catch(() =>
                    toast.error(t('console.hierarchy.hooks.page.copyCodeErrorToast'))
                );
        },
        [toast, t]
    );

    /* ------------------------------------------- level / assignment CRUD */

    const [levelModalOpen, setLevelModalOpen] = useState(false);
    const [levelForm, setLevelForm] = useState({ name: '', role_key: '', description: '' });
    const [levelError, setLevelError] = useState<string | null>(null);
    const [assignModalOpen, setAssignModalOpen] = useState(false);
    const [assignForm, setAssignForm] = useState({ user_sub: '', user_email: '', level_id: '' });
    const [assignError, setAssignError] = useState<string | null>(null);
    const [confirmDeactivateLevel, setConfirmDeactivateLevel] = useState<OrgLevel | null>(null);
    const [confirmRevokeAssignment, setConfirmRevokeAssignment] = useState<OrgAssignment | null>(
        null
    );

    const createLevel = useMutation({
        mutationFn: () =>
            createOrgLevel(config.apiBase, accessToken as string, orgId as string, {
                name: levelForm.name.trim(),
                role_key: levelForm.role_key.trim(),
                ...(levelForm.description.trim()
                    ? { description: levelForm.description.trim() }
                    : {}),
            }),
        onSuccess: (level) => {
            setLevelForm({ name: '', role_key: '', description: '' });
            setLevelError(null);
            setLevelModalOpen(false);
            toast.success(
                t('console.hierarchy.hooks.page.createLevelSuccessToast', { name: level.name })
            );
            void invalidateLevels();
        },
        onError: (error) =>
            setLevelError(
                errMessage(error, t('console.hierarchy.hooks.page.createLevelErrorFallback'))
            ),
    });

    const deactivateLevel = useMutation({
        mutationFn: (levelId: string) =>
            deactivateOrgLevel(config.apiBase, accessToken as string, orgId as string, levelId),
        onSuccess: (level) => {
            setConfirmDeactivateLevel(null);
            toast.success(
                t('console.hierarchy.hooks.page.deactivateLevelSuccessToast', {
                    name: level.name,
                })
            );
            void invalidateLevels();
        },
        onError: (error) =>
            toast.error(
                errMessage(
                    error,
                    t('console.hierarchy.hooks.page.deactivateLevelErrorFallback')
                )
            ),
    });

    const createAssignment = useMutation({
        mutationFn: () =>
            createOrgAssignment(config.apiBase, accessToken as string, orgId as string, {
                user_sub: assignForm.user_sub.trim(),
                ...(assignForm.user_email.trim()
                    ? { user_email: assignForm.user_email.trim() }
                    : {}),
                level_id: assignForm.level_id,
            }),
        onSuccess: (assignment) => {
            setAssignForm({ user_sub: '', user_email: '', level_id: '' });
            setAssignError(null);
            setAssignModalOpen(false);
            toast.success(
                t('console.hierarchy.hooks.page.createAssignmentSuccessToast', {
                    identity: assignment.user_email || assignment.user_sub,
                })
            );
            void invalidateAssignments();
        },
        onError: (error) =>
            setAssignError(
                errMessage(error, t('console.hierarchy.hooks.page.createAssignmentErrorFallback'))
            ),
    });

    const revokeAssignment = useMutation({
        mutationFn: (assignmentId: string) =>
            revokeOrgAssignment(
                config.apiBase,
                accessToken as string,
                orgId as string,
                assignmentId
            ),
        onSuccess: (assignment) => {
            setConfirmRevokeAssignment(null);
            toast.success(
                t('console.hierarchy.hooks.page.revokeAssignmentSuccessToast', {
                    identity: assignment.user_email || assignment.user_sub,
                })
            );
            void invalidateAssignments();
        },
        onError: (error) =>
            toast.error(
                errMessage(error, t('console.hierarchy.hooks.page.revokeAssignmentErrorFallback'))
            ),
    });

    function onCreateLevel(e: FormEvent) {
        e.preventDefault();
        if (!levelForm.name.trim() || !levelForm.role_key.trim()) {
            setLevelError(t('console.hierarchy.hooks.page.levelValidationNameRole'));
            return;
        }
        createLevel.mutate();
    }
    function onCreateAssignment(e: FormEvent) {
        e.preventDefault();
        if (!assignForm.user_sub.trim() || !assignForm.level_id) {
            setAssignError(t('console.hierarchy.hooks.page.assignValidationRequired'));
            return;
        }
        createAssignment.mutate();
    }

    return {
        // context
        org,
        rootOrg,
        canView,
        canManage,
        canManageTree,
        // query passthrough
        levelsQuery,
        levelBreakdownQuery,
        levels,
        activeLevels,
        // frame 29 toolbar meta — see the `unitCount` memo for why it is a floor
        unitCount,
        unitCountPartial,
        // tree
        rootOrgQuery,
        tree,
        visibleNodes,
        knownOrgs,
        childrenByParent,
        rootTruncated,
        expandedIds,
        toggleExpanded,
        collapseAll,
        isLoadingRoot,
        isErrorRoot,
        visibleLevels,
        levelBreakdown,
        // search
        searchQuery,
        setSearchQuery,
        searchActive,
        searchResults,
        searchResultsQuery,
        searchTruncated,
        // selection
        selectedOrgId,
        setSelectedOrgId,
        selectedNode,
        selectedCoverageQuery,
        selectedAchScopes,
        selectedScopesQuery,
        // drag (see useHierarchyDrag.ts)
        ...drag,
        // move (see useOrgMove.ts)
        ...moveVM,
        // unit types (see useOrgUnitTypes.ts). Every member it returns is
        // `unitType*`/`retype*` prefixed precisely so this spread cannot shadow
        // the org-rename and assignment-form state below, which own the bare
        // `renameDraft` / `setRenameDraft` / `assignError` names.
        ...unitTypesVM,
        moveDialogNode,
        openMoveDialog,
        closeMoveDialog,
        adoptDialogOrg,
        openAdoptDialog,
        closeAdoptDialog,
        // org create
        createOpen,
        createForm,
        createError,
        openCreateChild,
        closeCreate,
        onCreateNameChange,
        setCreateForm,
        onSubmitCreate,
        createChildOrg,
        // org rename (inline)
        renamingId,
        renameDraft,
        setRenameDraft,
        renameError,
        beginRename,
        cancelRename,
        commitRename,
        renameOrg,
        // org deactivate / reactivate
        confirmDeactivateOrgId,
        setConfirmDeactivateOrgId,
        confirmDeactivateNode,
        deactivatePreflightQuery,
        deactivateOrg,
        reactivateOrg,
        copyOrgCode,
        // level/assignment CRUD (unchanged behaviour)
        levelModalOpen,
        setLevelModalOpen,
        levelForm,
        setLevelForm,
        levelError,
        onCreateLevel,
        createLevel,
        assignModalOpen,
        setAssignModalOpen,
        assignForm,
        setAssignForm,
        assignError,
        onCreateAssignment,
        createAssignment,
        confirmDeactivateLevel,
        setConfirmDeactivateLevel,
        deactivateLevel,
        confirmRevokeAssignment,
        setConfirmRevokeAssignment,
        revokeAssignment,
    };
}

export type HierarchyPageVM = ReturnType<typeof useHierarchyPage>;
